/**
 * Time Entries Hooks
 *
 * React Query hooks for time entry CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTeam } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import {
  listTimeEntries,
  createManualTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  getTotalTimeForWorkOrder,
} from '../db/timeEntries';
import type { ManualTimeEntryInput, TimeEntryUpdateInput } from '../types/timeEntry';

/**
 * Query key factory for time entries
 */
export const timeEntryKeys = {
  all: (teamId: string) => ['timeEntries', teamId] as const,
  workOrder: (teamId: string, workOrderId: string) => ['timeEntries', teamId, workOrderId] as const,
  totals: (teamId: string, workOrderId: string) =>
    ['timeEntries', teamId, workOrderId, 'totals'] as const,
};

/**
 * Hook to list time entries for a work order
 */
export function useTimeEntries(workOrderId: string | undefined) {
  const { activeTeamId } = useTeam();

  return useQuery({
    queryKey: timeEntryKeys.workOrder(activeTeamId || '', workOrderId || ''),
    queryFn: async () => {
      if (!activeTeamId || !workOrderId) return [];
      return listTimeEntries(activeTeamId, workOrderId);
    },
    enabled: !!activeTeamId && !!workOrderId,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to get total time for a work order
 */
export function useTimeEntryTotals(workOrderId: string | undefined) {
  const { activeTeamId } = useTeam();

  return useQuery({
    queryKey: timeEntryKeys.totals(activeTeamId || '', workOrderId || ''),
    queryFn: async () => {
      if (!activeTeamId || !workOrderId) {
        return { totalMinutes: 0, totalCost: 0 };
      }
      return getTotalTimeForWorkOrder(activeTeamId, workOrderId);
    },
    enabled: !!activeTeamId && !!workOrderId,
    staleTime: 30_000,
  });
}

/**
 * Invalidate time entry queries for a work order
 */
function invalidateTimeEntryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  teamId: string,
  workOrderId: string
) {
  void queryClient.invalidateQueries({
    queryKey: timeEntryKeys.workOrder(teamId, workOrderId),
  });
  void queryClient.invalidateQueries({
    queryKey: timeEntryKeys.totals(teamId, workOrderId),
  });
  // Also invalidate work order queries (totals may have changed)
  void queryClient.invalidateQueries({
    queryKey: ['workOrders', teamId],
  });
}

/**
 * Hook to create a manual time entry
 */
export function useCreateManualTimeEntry(workOrderId: string) {
  const { activeTeamId } = useTeam();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ManualTimeEntryInput) => {
      if (!activeTeamId) throw new Error('No active team');
      if (!user) throw new Error('No authenticated user');

      return createManualTimeEntry(activeTeamId, workOrderId, input, user.uid);
    },
    onSuccess: () => {
      if (activeTeamId) {
        invalidateTimeEntryQueries(queryClient, activeTeamId, workOrderId);
      }
      logger.info('[useCreateManualTimeEntry] Time entry created');
    },
    onError: (error) => {
      logger.error('[useCreateManualTimeEntry] Failed to create time entry:', error);
    },
  });
}

/**
 * Hook to update a time entry
 */
export function useUpdateTimeEntry(workOrderId: string) {
  const { activeTeamId } = useTeam();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      timeEntryId,
      input,
    }: {
      timeEntryId: string;
      input: TimeEntryUpdateInput;
    }) => {
      if (!activeTeamId) throw new Error('No active team');

      return updateTimeEntry(activeTeamId, workOrderId, timeEntryId, input);
    },
    onSuccess: () => {
      if (activeTeamId) {
        invalidateTimeEntryQueries(queryClient, activeTeamId, workOrderId);
      }
      logger.info('[useUpdateTimeEntry] Time entry updated');
    },
    onError: (error) => {
      logger.error('[useUpdateTimeEntry] Failed to update time entry:', error);
    },
  });
}

/**
 * Hook to delete a time entry
 */
export function useDeleteTimeEntry(workOrderId: string) {
  const { activeTeamId } = useTeam();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timeEntryId: string) => {
      if (!activeTeamId) throw new Error('No active team');

      return deleteTimeEntry(activeTeamId, workOrderId, timeEntryId);
    },
    onSuccess: () => {
      if (activeTeamId) {
        invalidateTimeEntryQueries(queryClient, activeTeamId, workOrderId);
      }
      logger.info('[useDeleteTimeEntry] Time entry deleted');
    },
    onError: (error) => {
      logger.error('[useDeleteTimeEntry] Failed to delete time entry:', error);
    },
  });
}
